import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable, throwError, timer, race } from 'rxjs'
import { finalize, shareReplay, catchError, switchMap, filter, take, tap } from 'rxjs/operators'

import * as api from '@/config/api-config.json'
import { log, warn, error } from '@/util/logger'

@Injectable({ providedIn: 'root' })
export class TokenRefreshService {
  private _inFlight$: Observable<any> = null
  private _lastWaitStartTime: number = 0
  private readonly LOCK_KEY = 'token_refresh_lock'
  private readonly LOCK_TIMEOUT = 15000 // 15 seconds - max time to hold lock
  private readonly WAIT_TIMEOUT = 20000 // 20 seconds - max time to wait for another tab
  private readonly LOCK_CHECK_INTERVAL = 100 // Check every 100ms

  constructor(private _http: HttpClient) {}

  refreshToken(refreshToken?: string): Observable<any> {
    log('[TokenRefreshService] Refresh token requested', { 
      hasRefreshToken: !!refreshToken,
      hasInFlight: !!this._inFlight$ 
    })

    if (this._inFlight$) {
      log('[TokenRefreshService] Refresh already in progress, reusing existing request')
      return this._inFlight$
    }

    // Try to acquire cross-tab lock
    if (this._tryAcquireLock()) {
      // This tab acquired the lock - perform the refresh
      log('[TokenRefreshService] Lock acquired, performing refresh')
      const body = refreshToken ? { refresh_token: refreshToken } : {}

      const requestStartTime = Date.now()
      log('[TokenRefreshService] Sending refresh request to server', {
        hasRefreshToken: !!refreshToken
      })
      
      this._inFlight$ = this._http.post<any>(api.auth.refresh, body).pipe(
        tap(response => {
          const duration = Date.now() - requestStartTime
          log('[TokenRefreshService] Refresh response received', {
            hasToken: !!response?.token,
            hasRefreshToken: !!response?.refresh_token,
            hasUser: !!response?.user,
            duration: `${duration}ms`
          })
        }),
        shareReplay(1),
        catchError(err => {
          const duration = Date.now() - requestStartTime
          error('[TokenRefreshService] Refresh request failed', {
            error: err?.error || err,
            status: err?.status,
            duration: `${duration}ms`
          })
          this._inFlight$ = null
          this._releaseLock()
          return throwError(err)
        }),
        finalize(() => {
          const duration = Date.now() - requestStartTime
          log('[TokenRefreshService] Refresh request completed, releasing lock', {
            duration: `${duration}ms`
          })
          this._inFlight$ = null
          this._releaseLock()
        })
      )

      return this._inFlight$
    } else {
      // Another tab is refreshing - wait for storage event with new token
      log('[TokenRefreshService] Another tab is refreshing, waiting for result')
      return this._waitForRefreshCompletion()
    }
  }

  private _tryAcquireLock(): boolean {
    try {
      const now = Date.now()
      const lockData = localStorage.getItem(this.LOCK_KEY)

      if (lockData) {
        const lockTime = parseInt(lockData, 10)
        const lockAge = now - lockTime

        // Check if lock is extremely stale (e.g., from a crashed tab)
        if (lockAge > 60000) { // 1 minute
          warn(`[TokenRefreshService] Lock is extremely stale (age: ${lockAge}ms), forcefully taking it`)
          // Continue to acquire lock
        } else if (lockAge < this.LOCK_TIMEOUT) {
          log(`[TokenRefreshService] Lock held by another tab (age: ${lockAge}ms)`)
          return false // Lock is held by another tab
        } else {
          // Lock is stale, we can take it
          log(`[TokenRefreshService] Lock is stale (age: ${lockAge}ms), taking it`)
        }
      }

      // Acquire the lock
      localStorage.setItem(this.LOCK_KEY, now.toString())
      log('[TokenRefreshService] Lock acquired successfully')
      return true
    } catch (e) {
      warn('[TokenRefreshService] Failed to acquire lock', e)
      return true // Proceed anyway if localStorage fails
    }
  }

  private _releaseLock(): void {
    try {
      localStorage.removeItem(this.LOCK_KEY)
      log('[TokenRefreshService] Lock released')
    } catch (e) {
      warn('[TokenRefreshService] Failed to release lock', e)
    }
  }

  private _waitForRefreshCompletion(): Observable<any> {
    // Wait for either:
    // 1. Storage event indicating currentUser updated (successful refresh)
    // 2. Lock release without update (failed refresh)
    // 3. Timeout
    return new Observable(observer => {
      this._lastWaitStartTime = Date.now()
      log('[TokenRefreshService] Waiting for refresh completion from another tab', {
        timeout: `${this.WAIT_TIMEOUT}ms`
      })
      
      const timeout = setTimeout(() => {
        const waitDuration = Date.now() - (this._lastWaitStartTime || Date.now())
        error('[TokenRefreshService] Timeout waiting for refresh from another tab', {
          waitDuration: `${waitDuration}ms`,
          timeout: `${this.WAIT_TIMEOUT}ms`
        })
        cleanup()
        const timeoutError = new Error('Token refresh timeout')
        timeoutError.name = 'TimeoutError'
        observer.error(timeoutError)
      }, this.WAIT_TIMEOUT) // Use longer timeout for waiting

      const storageHandler = (event: StorageEvent) => {
        if (event.key === 'currentUser' && event.newValue) {
          try {
            const user = JSON.parse(event.newValue)
            if (user && user.token) {
              const waitDuration = Date.now() - (this._lastWaitStartTime || Date.now())
              log('[TokenRefreshService] Received updated tokens from another tab via storage event', {
                userId: user.id,
                hasToken: !!user.token,
                hasRefreshToken: !!user.refresh_token,
                waitDuration: `${waitDuration}ms`
              })
              cleanup()
              observer.next({ token: user.token, refresh_token: user.refresh_token, user: user })
              observer.complete()
            }
          } catch (e) {
            warn('[TokenRefreshService] Failed to parse user from storage event', e)
          }
        }
      }

      const lockCheckInterval = setInterval(() => {
        const lockData = localStorage.getItem(this.LOCK_KEY)
        if (!lockData) {
          // Lock released but no storage event = refresh failed
          cleanup()
          observer.error({ error: 'Token refresh failed in another tab' })
        }
      }, this.LOCK_CHECK_INTERVAL)

      window.addEventListener('storage', storageHandler)

      const cleanup = () => {
        clearTimeout(timeout)
        clearInterval(lockCheckInterval)
        window.removeEventListener('storage', storageHandler)
      }
    })
  }
}
